import React from 'react';
import * as XLSX from 'xlsx';
import { RowData } from '../App';
import { Block } from '../classes/Block';
import { VideoSettings } from './VideoSettings';
import { t } from '@lingui/core/macro';
import { auth } from '../firebase';
import { buildTableData, TableMatrix } from '../utils/excelTable';
import { getApiUrl } from '../utils/api';
import { sanitizeFileNameSegment } from '../utils/stringSanitization';
import { useProject } from '../contexts/ProjectContext';

// Add type declarations for File System Access API
declare global {
  interface Window {
    showDirectoryPicker(): Promise<FileSystemDirectoryHandle>;
  }
}

interface FileSystemDirectoryHandle {
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<FileSystemDirectoryHandle>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileSystemFileHandle>;
}

interface FileSystemFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>;
}

interface FileSystemWritableFileStream {
  write(data: Blob): Promise<void>;
}

interface ExcelExporterProps {
  rowData: RowData[];
  columnDefs: any[];
  sheetName: string;
  blocks?: Block[];
  onExport?: () => void;
  videoSettings: VideoSettings;
}

// Utility function to save to Google Sheets - can be called from outside the component
export const saveToGoogleSheets = async (aoaData: TableMatrix, sheetName: string, userEmail: string | null, projectId?: string) => {
  if (!userEmail) {
    console.log('[GSHEETS] No user email available, skipping Google Sheets save');
    return;
  }

  try {
    const response = await fetch(`${getApiUrl()}/save-excel?projectId=${projectId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        values: aoaData,
        sheetName: sheetName || 'Sheet1',
        userEmail,
        sheetsOnly: true
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save to Google Sheets');
    }

    const result = await response.json();
    console.log('[GSHEETS] Saved to Google Sheets:', result);
  } catch (error) {
    console.error('[GSHEETS] Error saving to Google Sheets:', error);
  }
};

const ExcelExporter: React.FC<ExcelExporterProps> = ({ 
  rowData, 
  columnDefs, 
  sheetName,
  blocks = [],
  onExport,
  videoSettings
}) => {
  const { selectedProject } = useProject();

  const buildExportFileName = (): string => {
    const sanitizedProjectName: string = sanitizeFileNameSegment(selectedProject?.name, {
      fallback: 'project',
      lowercase: false,
      replacements: { ' ': '_', ':': 'x' },
      invalidReplacement: '_',
    });

    const now: Date = new Date();
    const day: string = now.getDate().toString().padStart(2, '0');
    const month: string = (now.getMonth() + 1).toString().padStart(2, '0');
    const year: string = now.getFullYear().toString();
    const hours: string = now.getHours().toString().padStart(2, '0');
    const minutes: string = now.getMinutes().toString().padStart(2, '0');

    const timestamp: string = `${day}-${month}-${year} ${hours}-${minutes}`;
    return `${sanitizedProjectName} ${timestamp}.xlsx`;
  };

  const exportToExcel = async () => {
    if (rowData.length === 0 || columnDefs.length === 0) {
      alert('No data to export');
      return;
    }

    try {
      const aoaData: TableMatrix = buildTableData(blocks, columnDefs, videoSettings);
      
      // Create worksheet from array of arrays
      const worksheet = XLSX.utils.aoa_to_sheet(aoaData);
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      const fileName: string = buildExportFileName();
      
      // Generate Excel file for download
      XLSX.writeFile(workbook, fileName);

      // Save to local data folder
      try {
        // Convert workbook to binary string
        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' });
        
        // Convert binary string to Blob
        const blob = new Blob([s2ab(wbout)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Create FormData and append the blob
        const formData = new FormData();
        formData.append('file', blob, fileName);
        
        // Send the file to the server
        const response = await fetch(`${getApiUrl()}/save-excel?projectId=${selectedProject!.id}`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save file locally');
        }
        
        const result = await response.json();
        console.log('File saved successfully:', result.path);
        
        // Call the onExport callback after successful export
        if (onExport) {
          onExport();
        }
      } catch (fsError) {
        console.error('Error saving to local file system:', fsError);
        // Don't show an alert for this error as it's not critical
      }

      // Also save to Google Sheets via the unified endpoint
      try {
        const userEmail: string | null = auth.currentUser?.email ?? null;
        if (userEmail) {
          await saveToGoogleSheets(aoaData, sheetName, userEmail, selectedProject!.id);
        }
      } catch (gsErr) {
        console.error('Error saving to Google Sheets:', gsErr);
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export data to Excel.');
    }
  };

  // Helper function to convert string to ArrayBuffer
  const s2ab = (s: string) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) {
      view[i] = s.charCodeAt(i) & 0xFF;
    }
    return buf;
  };

  return (
    <div className="excel-exporter">
      <button 
        onClick={exportToExcel} 
        disabled={rowData.length === 0}
        className="export-button"
      >
        {t`Backup to Excel`}
      </button>
    </div>
  );
};

export default ExcelExporter; 